import { Link } from "react-router-dom";
import {
  HelpCircle, Gauge, ScanLine, BookOpen, History, Wrench, Camera, MoveHorizontal, Upload,
  Sparkles, Zap, Save, Smartphone, Maximize2, ArrowRight,
} from "lucide-react";

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="shrink-0 w-6 h-6 rounded-sm bg-amber/15 text-amber font-mono text-xs flex items-center justify-center font-bold">{n}</span>
    <span className="text-sm text-slate-300 leading-relaxed">{children}</span>
  </li>
);

const Section = ({ icon: Icon, title, tag, children }) => (
  <section className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={18} className="text-amber" />
      <h2 className="font-heading font-bold uppercase tracking-widest text-lg">{title}</h2>
    </div>
    {tag && <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-4">{tag}</div>}
    <div className="mt-3">{children}</div>
  </section>
);

const K = ({ children }) => <span className="text-amber font-mono">{children}</span>;

export default function Instructions() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 animate-rise">
        <div className="flex items-center gap-2 font-mono text-xs text-amber tracking-widest uppercase">
          <HelpCircle size={14} /> Getting Started
        </div>
        <h1 className="font-heading font-black uppercase tracking-wider text-3xl sm:text-4xl lg:text-5xl mt-2">How To Use</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          A quick tour of every tool in Thortek Performance — from entering readings to measuring with the camera.
        </p>
      </div>

      {/* quick start */}
      <div className="rounded-md border border-amber/40 bg-amber/5 p-5 mb-6 animate-rise">
        <div className="font-mono text-[10px] text-amber tracking-widest uppercase mb-3">60-Second Quick Start</div>
        <ol className="space-y-3">
          <Step n="1">Open <K>Visual Scan</K> and measure your wheels with the camera — or just type readings straight into the <K>Calculator</K>.</Step>
          <Step n="2">The Calculator instantly scores each angle against your vehicle's factory specs and shows an overall score.</Step>
          <Step n="3">Tap <K>Save Session</K> to log the result. Use the <K>DIY Guide</K> to make adjustments, then re-measure.</Step>
        </ol>
      </div>

      <div className="space-y-6">
        <Section icon={Gauge} title="Calculator" tag="Enter readings · live pass/fail scoring">
          <ul className="space-y-3">
            <Step n="1">Type your measured <K>camber</K>, <K>caster</K> and <K>toe</K> for each corner, or use the <K>+ / −</K> steppers.</Step>
            <Step n="2">Each row shows <K>IN SPEC</K>, <K>MARGINAL</K> or <K>OUT OF SPEC</K>, and the chassis diagram tilts/rotates the wheels live.</Step>
            <Step n="3">The score card gives an overall alignment %. Tap <K>Load Factory Specs</K> (<Zap size={12} className="inline" />) to preview ideal targets, or <K>Reset</K> to clear.</Step>
            <Step n="4">Tap <K>Save Session</K> (<Save size={12} className="inline" />) to store the readings, mileage and notes.</Step>
          </ul>
          <p className="text-xs text-slate-500 mt-4 font-mono">Readings are remembered on this device, so scans and manual edits stay in sync.</p>
        </Section>

        <Section icon={ScanLine} title="Visual Scan" tag="Measure alignment with the camera">
          <p className="text-sm text-slate-300 leading-relaxed mb-4">Three ways to capture a reading — pick a <K>Scan Source</K> at the top left:</p>

          <div className="space-y-4">
            <div className="rounded-sm bg-ink border border-steel/60 p-4">
              <div className="flex items-center gap-2 font-mono text-xs text-amber uppercase tracking-widest mb-2"><Camera size={14} /> Live Camera — single wheel</div>
              <ul className="space-y-2">
                <Step n="1">Pick the <K>corner</K> (FL/FR/RL/RR) and whether you're measuring <K>camber</K> or <K>toe</K>.</Step>
                <Step n="2">Point at the wheel and <K>drag the guide line</K> (or use the slider) to match the tyre; the angle reads out live.</Step>
                <Step n="3">Tap <K>Fullscreen</K> (<Maximize2 size={12} className="inline" />) for a bigger view, then <K>Capture</K>.</Step>
                <Step n="4">Optionally tap <K>AI Detect</K> (<Sparkles size={12} className="inline" />) to let AI estimate the angle, then <K>Apply to Calculator</K>.</Step>
              </ul>
              <p className="text-xs text-sky mt-3 font-mono flex items-center gap-1"><Smartphone size={12} /> Tip: enable the Phone Tilt Gauge and rest the phone flat on the wheel face for a quick camber reading.</p>
            </div>

            <div className="rounded-sm bg-ink border border-steel/60 p-4">
              <div className="flex items-center gap-2 font-mono text-xs text-amber uppercase tracking-widest mb-2"><MoveHorizontal size={14} /> Pano — whole axle at once</div>
              <ul className="space-y-2">
                <Step n="1">Choose <K>Front</K> or <K>Rear</K> axle and tap <K>Start Axle Sweep</K>.</Step>
                <Step n="2"><K>Slowly pan</K> the camera from the left wheel across to the right wheel — keep both wheels in frame. The progress bar fills as you sweep.</Step>
                <Step n="3">When it finishes it stitches a wide panorama. Tap <K>AI Read Axle</K> to get left + right camber and total toe.</Step>
                <Step n="4">Tap <K>Apply Axle</K> to fill both wheels and the toe on the Calculator, and save the panorama with your session.</Step>
              </ul>
            </div>

            <div className="rounded-sm bg-ink border border-steel/60 p-4">
              <div className="flex items-center gap-2 font-mono text-xs text-amber uppercase tracking-widest mb-2"><Upload size={14} /> Upload — from a saved photo</div>
              <ul className="space-y-2">
                <Step n="1">Tap to upload a clear photo of the wheel (straight-on for camber).</Step>
                <Step n="2">Drag the guide to match, or tap <K>AI Detect</K>, then <K>Apply to Calculator</K>.</Step>
              </ul>
            </div>
          </div>

          <div className="rounded-sm border border-amber/40 bg-amber/5 p-3 mt-4">
            <p className="text-xs text-slate-300 leading-relaxed">
              <span className="text-amber font-mono">Camera won't open?</span> Use a normal browser tab (not the embedded preview) on <K>https</K>, and allow camera access when prompted. On Android Chrome: lock icon → Permissions → Camera → Allow. A camera estimate is a field guide — always confirm against a gauge before adjusting.
            </p>
          </div>
        </Section>

        <Section icon={BookOpen} title="Spec Vault" tag="Factory reference numbers">
          <p className="text-sm text-slate-300 leading-relaxed">
            Look up your vehicle's official front/rear camber, caster and toe ranges, plus ride-height (Z/D) and torque specs where available. These are the targets the Calculator scores against.
          </p>
        </Section>

        <Section icon={History} title="Session Log" tag="Your saved alignments">
          <ul className="space-y-3">
            <Step n="1">Every saved session appears here newest-first, with its pass %, key readings and any scan photos.</Step>
            <Step n="2">Once you have two or more sessions, a <K>drift chart</K> shows how your angles change over time.</Step>
            <Step n="3">Tap <K>Delete</K> on a card to remove a session.</Step>
          </ul>
        </Section>

        <Section icon={Wrench} title="DIY Guide" tag="Do the actual adjustment">
          <ul className="space-y-3">
            <Step n="1">Follow the string-line procedure step by step and tick each one off — the progress bar tracks you.</Step>
            <Step n="2">Torque callouts are included (cam bolts, tie-rod jam nuts). Always set the rear first, then the front.</Step>
            <Step n="3">Check the <K>Troubleshooting</K> section for pulls, uneven tyre wear and off-center steering.</Step>
          </ul>
        </Section>
      </div>

      <div className="flex flex-wrap gap-3 mt-8 animate-rise">
        <Link to="/scan" data-testid="help-go-scan"
          className="inline-flex items-center gap-2 rounded-sm bg-amber text-ink px-5 py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-amber-hover transition-colors">
          <ScanLine size={15} /> Try Visual Scan <ArrowRight size={14} />
        </Link>
        <Link to="/" data-testid="help-go-calculator"
          className="inline-flex items-center gap-2 rounded-sm bg-elevated border border-steel text-slate-200 px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-steel transition-colors">
          <Gauge size={15} /> Open Calculator
        </Link>
      </div>
    </div>
  );
}
